import { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from '../common/Button';
import { useProjects } from '../../hooks/useProjects';
import { useAuth } from '../../services/authContext';
import type { DetailedReportFilters as Filters } from '../../services/api/reports';
import { DateField } from '../ui/DateField';

interface DetailedReportFiltersProps {
  onApply: (filters: Filters) => void;
  isLoading?: boolean;
  initialFilters?: Filters | null;
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDate(dateStr: string): Date {
  const parts = dateStr.split('-');
  const year = parseInt(parts[0] ?? '0', 10);
  const month = parseInt(parts[1] ?? '1', 10) - 1;
  const day = parseInt(parts[2] ?? '1', 10);
  return new Date(year, month, day);
}

function getDefaultDates() {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: formatDate(firstOfMonth),
    to: formatDate(lastOfMonth),
  };
}

function getPreviousMonthDates(currentFrom: string) {
  const current = parseDate(currentFrom);
  const firstOfPrevMonth = new Date(current.getFullYear(), current.getMonth() - 1, 1);
  const lastOfPrevMonth = new Date(current.getFullYear(), current.getMonth(), 0);
  return {
    from: formatDate(firstOfPrevMonth),
    to: formatDate(lastOfPrevMonth),
  };
}

function getNextMonthDates(currentFrom: string) {
  const current = parseDate(currentFrom);
  const firstOfNextMonth = new Date(current.getFullYear(), current.getMonth() + 1, 1);
  const lastOfNextMonth = new Date(current.getFullYear(), current.getMonth() + 2, 0);
  return {
    from: formatDate(firstOfNextMonth),
    to: formatDate(lastOfNextMonth),
  };
}

function getCurrentQuarterDates() {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const firstOfQuarter = new Date(now.getFullYear(), currentQuarter * 3, 1);
  const lastOfQuarter = new Date(now.getFullYear(), currentQuarter * 3 + 3, 0);
  return {
    from: formatDate(firstOfQuarter),
    to: formatDate(lastOfQuarter),
  };
}

function getPreviousQuarterDates() {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  let year = now.getFullYear();
  let prevQuarter = currentQuarter - 1;

  if (prevQuarter < 0) {
    prevQuarter = 3;
    year -= 1;
  }

  const firstOfQuarter = new Date(year, prevQuarter * 3, 1);
  const lastOfQuarter = new Date(year, prevQuarter * 3 + 3, 0);
  return {
    from: formatDate(firstOfQuarter),
    to: formatDate(lastOfQuarter),
  };
}

export default function DetailedReportFilters({ onApply, isLoading, initialFilters }: DetailedReportFiltersProps) {
  const defaults = getDefaultDates();
  const [from, setFrom] = useState(initialFilters?.from || defaults.from);
  const [to, setTo] = useState(initialFilters?.to || defaults.to);
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>(initialFilters?.projectIds || []);
  const [search, setSearch] = useState('');
  const [includeSubprojects, setIncludeSubprojects] = useState(initialFilters?.includeSubprojects ?? true);
  const [includeExpenses, setIncludeExpenses] = useState(initialFilters?.includeExpenses ?? false);
  const [includeExtraGains, setIncludeExtraGains] = useState(initialFilters?.includeExtraGains ?? false);
  const [includeCosts, setIncludeCosts] = useState(initialFilters?.includeCosts ?? false);
  const [includeNotApproved, setIncludeNotApproved] = useState(initialFilters?.includeNotApproved ?? false);
  const [includeRejected, setIncludeRejected] = useState(initialFilters?.includeRejected ?? false);

  const { isAdmin, isManager } = useAuth();
  const onlyMine = !isAdmin && isManager;

  const { data: projects = [] } = useProjects({
    state: 'in_progress',
    ...(onlyMine ? { onlyMine: true } : {}),
  });

  const filteredProjects = useMemo(() => {
    if (!search) return projects;
    const searchLower = search.toLowerCase();
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        (p.fullName && p.fullName.toLowerCase().includes(searchLower))
    );
  }, [projects, search]);

  const handleApply = () => {
    if (from && to && selectedProjectIds.length > 0) {
      hasApplied.current = true;
      onApply({
        projectIds: selectedProjectIds,
        from,
        to,
        includeSubprojects,
        includeExpenses,
        includeExtraGains,
        includeCosts,
        includeNotApproved,
        includeRejected,
      });
    }
  };

  const handleReset = () => {
    const defaults = getDefaultDates();
    setFrom(defaults.from);
    setTo(defaults.to);
    setSelectedProjectIds([]);
    setSearch('');
    setIncludeSubprojects(true);
    setIncludeExpenses(false);
    setIncludeExtraGains(false);
    setIncludeCosts(false);
    setIncludeNotApproved(false);
    setIncludeRejected(false);
  };

  const hasApplied = useRef(!!initialFilters);
  const dateNav = useRef(false);

  const setDateRange = (dates: { from: string; to: string }) => {
    dateNav.current = true;
    setFrom(dates.from);
    setTo(dates.to);
  };

  useEffect(() => {
    if (dateNav.current && hasApplied.current && selectedProjectIds.length > 0) {
      onApply({
        projectIds: selectedProjectIds,
        from,
        to,
        includeSubprojects,
        includeExpenses,
        includeExtraGains,
        includeCosts,
        includeNotApproved,
        includeRejected,
      });
    }
    dateNav.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on date nav changes
  }, [from, to]);

  const toggleProject = (projectId: number) => {
    setSelectedProjectIds((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
    );
  };

  const selectAll = () => {
    setSelectedProjectIds(filteredProjects.map((p) => p.id));
  };

  const selectNone = () => {
    setSelectedProjectIds([]);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      {/* Date Range Row */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <DateField
            value={from}
            onChange={setFrom}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <DateField
            value={to}
            onChange={setTo}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setDateRange(getPreviousMonthDates(from))}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            &larr; Prev
          </button>
          <button
            type="button"
            onClick={() => setDateRange(getNextMonthDates(from))}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Next &rarr;
          </button>
          <button
            type="button"
            onClick={() => setDateRange(getCurrentQuarterDates())}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Q Current
          </button>
          <button
            type="button"
            onClick={() => setDateRange(getPreviousQuarterDates())}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Q Previous
          </button>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button
            size="sm"
            onClick={handleApply}
            isLoading={isLoading}
            disabled={!from || !to || selectedProjectIds.length === 0}
          >
            Apply
          </Button>
          <Button size="sm" variant="secondary" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* Project Selection */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Projects ({selectedProjectIds.length} selected)
          </label>
          <div className="flex gap-2 text-sm">
            <button
              type="button"
              onClick={selectAll}
              className="text-primary-600 hover:text-primary-800"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={selectNone}
              className="text-primary-600 hover:text-primary-800"
            >
              Clear
            </button>
          </div>
        </div>
        <input
          type="text"
          placeholder="Search projects..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full mb-2 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
          {filteredProjects.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-2">No projects found</p>
          ) : (
            filteredProjects.map((project) => (
              <label
                key={project.id}
                className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedProjectIds.includes(project.id)}
                  onChange={() => toggleProject(project.id)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  {project.fullName || project.name}
                </span>
              </label>
            ))
          )}
        </div>
      </div>

      {/* Options */}
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeSubprojects}
            onChange={(e) => setIncludeSubprojects(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Include subprojects</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeExpenses}
            onChange={(e) => setIncludeExpenses(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Include expenses</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeExtraGains}
            onChange={(e) => setIncludeExtraGains(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Include extra gains</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeCosts}
            onChange={(e) => setIncludeCosts(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Include costs</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeNotApproved}
            onChange={(e) => setIncludeNotApproved(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Include not approved hours</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeRejected}
            onChange={(e) => setIncludeRejected(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Include rejected (rework) hours</span>
        </label>
      </div>

    </div>
  );
}
